import { Box, Container, Typography, Grid, Alert } from '@mui/material'
import { useAuth } from 'services/auth'
import { isOnPrem } from 'config/index'
import { ChipExportTool } from './ChipExportTool'
import { ChipImportTool } from './ChipImportTool'

/**
 * AdminToolsPage
 *
 * Container page for administrative tools.
 * Currently includes:
 * - Chip Export Tool (visible in GCP environments only)
 * - Chip Import Tool (visible in on-prem environments only)
 *
 * Access Control:
 * - In GCP environments: Only accessible to internal Deepcell users (isInternalUser === true)
 * - In ON_PREM environment: Accessible to all users
 */
function AdminToolsPage(): JSX.Element {
  const { isInternalUser, isLoading } = useAuth()

  // Show loading state while authentication is being determined
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  // In GCP environments, require internal user access
  // In ON_PREM environment, allow all users
  if (!isOnPrem && !isInternalUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access Denied: This page is only accessible to internal Deepcell users.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Tools
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Administrative tools for managing chip data across environments.
        </Typography>
      </Box>

      {/* Tool Sections */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {isOnPrem ? <ChipImportTool /> : <ChipExportTool />}
        </Grid>
      </Grid>
    </Container>
  )
}

export default AdminToolsPage
